import type { TypedAnalyticsContexts } from '@/analytics/snowplow/analytics-context.types';
import { AnalyticsTrackingProvider, useAnalyticsTracking } from '@/analytics/snowplow/AnalyticsTrackingContext';
import { useEmitUiEvent } from '@/analytics/snowplow/useEmitUIEvent';
import { buildContextId } from '@/analytics/snowplow/utils/buildContextId';
import { useAppStore } from '@/state-management';
import { Typography } from '@montugroup/design-system';
import ExpandMore from '@mui/icons-material/ExpandMore';
import type { PropsWithChildren } from 'react';
import { useCallback, useMemo } from 'react';
import { StyledAccordion, StyledAccordionDetails, StyledAccordionSummary } from './Section.styles';

interface SectionProps extends PropsWithChildren {
  title: string;
  sectionId: string;
}

export const Section = ({ title, sectionId, children }: SectionProps) => {
  const store = useAppStore();
  const defaultExpanded = useMemo(() => store.guidedDoctorConsult.openSections.includes(sectionId), []);
  const { uiEvent } = useAnalyticsTracking();

  const emitEvent = useEmitUiEvent();

  const updateTrackingContext = useCallback(
    (ctx: TypedAnalyticsContexts) => ({
      ...ctx,
      uiEvent: {
        context_id: buildContextId(ctx.uiEvent?.context_id ?? '', sectionId),
      },
    }),
    [sectionId],
  );

  const handleToggle = useCallback(
    (_e: React.SyntheticEvent, isExpanded: boolean) => {
      store.toggleSection(sectionId);

      emitEvent({
        component_name: 'SectionHeader',
        event_name: 'toggle_section',
        id: `${sectionId}_toggle`,
        value: isExpanded ? 'Expanded' : 'Collapsed',
        context_id: buildContextId(uiEvent?.context_id ?? '', sectionId),
      });
    },
    [emitEvent, sectionId, store, uiEvent],
  );

  return (
    <AnalyticsTrackingProvider update={updateTrackingContext}>
      <StyledAccordion defaultExpanded={defaultExpanded} onChange={handleToggle}>
        <StyledAccordionSummary expandIcon={<ExpandMore />}>
          <Typography>{title}</Typography>
        </StyledAccordionSummary>
        <StyledAccordionDetails data-testid={sectionId}>{children}</StyledAccordionDetails>
      </StyledAccordion>
    </AnalyticsTrackingProvider>
  );
};
